import { z } from "zod";
import { passportActionCountersSchema } from "./passport-progress.js";
import { skillStatusSchema } from "./progress.js";

export const platformMembershipKindSchema = z.enum(["employee", "supervisor", "admin"]);

export const platformReadinessSummarySchema = z.object({
  established: z.number().int().nonnegative(),
  onChecklist: z.number().int().nonnegative(),
  toTrain: z.number().int().nonnegative(),
  unassessed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  confirmedPercent: z.number().min(0).max(100),
});

export const platformOrganisationSummarySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  customerName: z.string().min(1),
  location: z.string().min(1),
  createdAt: z.string().datetime(),
  themeId: z.enum(["peer-start", "peer-start-warm"]),
  onboardingEnabled: z.boolean(),
  supervisorPersonaId: z.string().min(1).nullable(),
  counts: z.object({
    users: z.number().int().nonnegative(),
    employees: z.number().int().nonnegative(),
    supervisors: z.number().int().nonnegative(),
    admins: z.number().int().nonnegative(),
    roles: z.number().int().nonnegative(),
    pages: z.number().int().nonnegative(),
    stamps: z.number().int().nonnegative(),
    skills: z.number().int().nonnegative(),
    trainings: z.number().int().nonnegative(),
    pendingReviews: z.number().int().nonnegative(),
    onboardingOutstanding: z.number().int().nonnegative(),
  }),
  readiness: platformReadinessSummarySchema,
  passportActions: passportActionCountersSchema,
});

export const platformUserSummarySchema = z.object({
  identityId: z.string().min(1),
  personaId: z.string().min(1),
  organisationId: z.string().min(1),
  organisationName: z.string().min(1),
  email: z.string().email(),
  name: z.string().min(1),
  initials: z.string().min(1).max(3),
  kind: platformMembershipKindSchema,
  title: z.string().min(1),
  roleId: z.string().min(1).nullable(),
  roleTitle: z.string().min(1).nullable(),
  onboardingComplete: z.boolean(),
  workContext: z.string().nullable(),
});

export const platformActivitySummarySchema = z.object({
  id: z.string().min(1),
  organisationId: z.string().min(1),
  organisationName: z.string().min(1),
  employeeId: z.string().min(1),
  employeeName: z.string().min(1),
  type: z.string().min(1),
  summary: z.string().min(1),
  timestamp: z.string().datetime(),
  resultingStatus: skillStatusSchema.nullable(),
});

export const platformAdministrationSchema = z.object({
  generatedAt: z.string().datetime(),
  totals: z.object({
    organisations: z.number().int().nonnegative(),
    users: z.number().int().nonnegative(),
    employees: z.number().int().nonnegative(),
    supervisors: z.number().int().nonnegative(),
    admins: z.number().int().nonnegative(),
    pendingReviews: z.number().int().nonnegative(),
    establishedStamps: z.number().int().nonnegative(),
    trackedStamps: z.number().int().nonnegative(),
    confirmedPercent: z.number().min(0).max(100),
    passportActions: passportActionCountersSchema,
  }),
  organisations: z.array(platformOrganisationSummarySchema),
  users: z.array(platformUserSummarySchema),
  recentActivity: z.array(platformActivitySummarySchema),
});

export type PlatformMembershipKind = z.infer<typeof platformMembershipKindSchema>;
export type PlatformReadinessSummary = z.infer<typeof platformReadinessSummarySchema>;
export type PlatformOrganisationSummary = z.infer<typeof platformOrganisationSummarySchema>;
export type PlatformUserSummary = z.infer<typeof platformUserSummarySchema>;
export type PlatformActivitySummary = z.infer<typeof platformActivitySummarySchema>;
export type PlatformAdministration = z.infer<typeof platformAdministrationSchema>;
